import styled from "@emotion/styled";
import { css } from "@emotion/react";
import {
  BULLET_STYLE,
  COLUMN_ALIGN_CENTER__SPACE_B,
  COLUMN_ALIGN_START__JUSTIFY_START,
  COLUMN_CENTER,
  DESKTOP_WIDTH,
  GradientFontStyle,
  MOBILE_WIDTH,
  ROW_ALIGN_CENTER__SPACE_B,
  ROW_ALIGN_START__SPACE_B,
  STICKY_POSITION,
  TABLET_WIDTH,
} from "../../styles/global-styles";
import {
  TEXT_11,
  TEXT_16_H24,
  TEXT_18,
  TEXT_20_500,
  TEXT_24,
  TEXT_32,
  TEXT_36,
  TEXT_40,
  TEXT_50,
  TEXT_56,
  TEXT_64,
} from "../../styles/globalTypography";
import { breakpoints } from "../../styles/media-queries";
import bulletFirst from "../../assets/icons/bullet-first.svg";
import bulletSecond from "../../assets/icons/bullet-second.svg";
import bulletThird from "../../assets/icons/bullet-third.svg";

/* The site-wide reveal: a 500ms easeOutQuad visibility + opacity crossfade,
   flipped by an IntersectionObserver at a 0.15 threshold. */
const REVEAL_EASE = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

const reveal = (key) => (props) =>
  props?.containerVisibility?.[key]
    ? css`
        visibility: visible;
        opacity: 1;
        transition: visibility 0.5s ${REVEAL_EASE},
          opacity 0.5s ${REVEAL_EASE};
      `
    : css`
        visibility: hidden;
        opacity: 0;
        transition: visibility 0.5s ${REVEAL_EASE},
          opacity 0.5s ${REVEAL_EASE};
      `;

/* Nothing on this page animates for a reader who has asked it not to. */
const REDUCED_MOTION = css`
  @media (prefers-reduced-motion: reduce) {
    &,
    & * {
      animation: none !important;
      transition: none !important;
    }
  }
`;

/* The percentage-gutter container every section on the site is built on. */
const PAGE_CONTAINER = css`
  width: 100%;
  max-width: 1296px;
  margin: auto;
  @media (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.xxl}px) {
    ${DESKTOP_WIDTH}
  }
  @media (max-width: ${breakpoints.sm}px) {
    ${TABLET_WIDTH}
  }
  @media (max-width: ${breakpoints.xxs}px) {
    ${MOBILE_WIDTH}
  }
`;

const EYEBROW = css`
  min-width: unset;
  ${TEXT_11}
  text-transform: uppercase;
`;

/* The gradient clause of a headline. `display: inline-block` is load-bearing:
   background-clip on a *fragmented* inline paints only its first line box, so a
   clause that wraps loses every line after the first. One box paints them all. */
const GRADIENT_CLAUSE = css`
  ${GradientFontStyle}
  display: inline-block;
  max-width: 100%;
  padding-bottom: 1px;
`;

const GREY_BODY = css`
  ${TEXT_20_500}
  color: var(--Grey);
  @media (max-width: ${breakpoints.sm}px) {
    ${TEXT_18}
  }
  @media (max-width: ${breakpoints.xxs}px) {
    ${TEXT_16_H24}
  }
`;

/* The site's magnet markup contract: a wrapper with negative margin enlarging
   the hover target, and a child carrying the 0.75s linear chase. */
const MAGNET = css`
  .magnet-container {
    position: relative;
    text-align: center;
    cursor: pointer;
    margin: -5px;
  }
  .magnet-item {
    position: relative;
    display: inline-block;
    margin: 5px;
    transition: transform 0.75s linear;
    z-index: 1;
  }
`;

/* Primary CTA — the #1066df → #1555b0 ink fill used by the header and the
   business-integration section. */
const PRIMARY_BUTTON = css`
  padding: 20px 40px;
  min-width: 183px;
  height: 64px;
  background: linear-gradient(to left, rgba(16, 102, 223, 1) 50%, #1555b0 50%);
  background-size: 200% 100%;
  background-position: right;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    transition: all 0.25s ease;
    border-radius: 100px;
    transition-duration: 0.5s;
    box-shadow: inset 0 0 0 1px #1555b0;
  }
  &:hover::before {
    background-color: transparent;
    box-shadow: inset 0 0 0 64px #1555b0;
  }
  &:hover {
    box-shadow: unset;
    background-color: unset;
  }
`;

const GHOST_BUTTON = css`
  padding: 20px 40px;
  min-width: 190px;
  height: 64px;
  border: 2px solid var(--Black);
  border-radius: 100px;
  background: linear-gradient(
    to left,
    transparent 50%,
    rgba(255, 255, 255, 0.1) 50%
  );
  background-size: 200% 100%;
  background-position: right;
`;

const BUTTON_LABEL = css`
  line-height: 24px;
  letter-spacing: -0.02em;
  text-transform: none;
  white-space: nowrap;
  z-index: 1;
  position: relative;
`;

/* ── page shell ─────────────────────────────────────────────────────────── */

export const ConnectorStyled = styled("main")`
  display: block;
  width: 100%;
  position: relative;
`;

/* ── 1. hero ────────────────────────────────────────────────────────────── */

export const HeroSection = styled("section")`
  ${COLUMN_ALIGN_START__JUSTIFY_START};
  ${PAGE_CONTAINER}
  position: relative;
  gap: 48px;
  padding: 93px 0 280px;
  ${MAGNET}
  ${REDUCED_MOTION}

  @media (max-width: ${breakpoints.sm}px) {
    align-items: center;
    text-align: center;
    padding: 64px 0 220px;
  }
  @media (max-width: ${breakpoints.xxs}px) {
    gap: 40px;
    padding: 48px 0 200px;
  }

  & .connector-badge-text {
    ${EYEBROW}
    color: var(--Blue);
  }

  & .hero-title {
    width: 100%;
    max-width: 1100px;
    ${TEXT_64}
    font-weight: 700;
    color: var(--Black);
    @media (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.xxl}px) {
      ${TEXT_56}
    }
    @media (max-width: ${breakpoints.sm}px) {
      ${TEXT_40}
      line-height: 48px;
      text-align: center;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      ${TEXT_36}
      line-height: 41px;
      letter-spacing: -0.07em;
      text-align: center;
    }
  }

  & .gradient-font {
    ${GRADIENT_CLAUSE}
  }

  & .hero-body {
    ${GREY_BODY}
    max-width: 822px;
    @media (max-width: ${breakpoints.sm}px) {
      text-align: center;
    }
  }

  & .button-container {
    ${ROW_ALIGN_CENTER__SPACE_B}
    gap: 24px;
    max-width: 420px;
    @media (max-width: ${breakpoints.xxs}px) {
      flex-direction: column;
      width: 100%;
      max-width: unset;
      align-items: stretch;
    }
  }

  & .contained-button {
    ${PRIMARY_BUTTON}
  }
  & .WBorder-button {
    ${GHOST_BUTTON}
  }
  & .button-label {
    ${BUTTON_LABEL}
  }

  & .hero-trademark {
    position: absolute;
    z-index: -10;
    right: -339px;
    top: -180px;
    pointer-events: none;
    user-select: none;
    @media (max-width: ${breakpoints.sm}px) {
      display: none;
    }
  }

  ${reveal("connectorHero")}
`;

/* ── 2. passwordless band ───────────────────────────────────────────────── */

export const PasswordlessSection = styled("section")`
  background-color: var(--Alice-Blue);
  padding: 141px 0 142px;
  border-radius: 16px;
  margin-bottom: 382px;
  position: relative;
  z-index: 1;
  ${REDUCED_MOTION}

  @media (max-width: ${breakpoints.m1024}px) {
    margin-bottom: 191px;
  }
  @media (max-width: ${breakpoints.sm}px) {
    padding: 148px 0;
    border-radius: 0;
    margin-bottom: 151px;
  }
  @media (max-width: ${breakpoints.xxs}px) {
    padding: 132px 0;
    border-radius: 0;
    margin-bottom: 144px;
  }

  & .centerContent {
    position: relative;
    ${COLUMN_ALIGN_CENTER__SPACE_B};
    gap: 48px;
    max-width: 1081px;
    margin: auto;
    @media (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.xxl}px) {
      ${DESKTOP_WIDTH}
    }
    @media (max-width: ${breakpoints.sm}px) {
      width: 89%;
      gap: 56px;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      ${MOBILE_WIDTH}
      gap: 56px;
    }
  }

  & .connector-badge-text {
    ${EYEBROW}
    color: var(--Blue);
    text-align: center;
  }

  & .band-title {
    max-width: 100%;
    ${TEXT_56}
    font-weight: 700;
    color: var(--Black);
    text-align: center;
    @media (max-width: ${breakpoints.sm}px) {
      ${TEXT_40}
      line-height: 56px;
      text-align: center;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      ${TEXT_36}
      line-height: 44px;
      letter-spacing: -0.07em;
      text-align: center;
    }
  }

  & .gradient-font {
    ${GRADIENT_CLAUSE}
  }

  & .band-body {
    width: 100%;
    ${GREY_BODY}
    text-align: center;
  }

  & .logoTop {
    position: absolute;
    top: -242px;
    left: -183px;
    width: 264px;
    pointer-events: none;
    @media (max-width: ${breakpoints.sm}px) {
      width: 220px;
      top: -230px;
      left: -93px;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      width: 180px;
      top: -196px;
      left: -60px;
    }
  }

  & .logoBottom {
    position: absolute;
    bottom: -305px;
    right: -141px;
    width: 248px;
    pointer-events: none;
    @media (max-width: ${breakpoints.sm}px) {
      width: 242.42px;
      bottom: -312px;
      right: -100px;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      width: 190px;
      bottom: -250px;
      right: -50px;
    }
  }

  .floating {
    animation-name: floating;
    animation-duration: 5s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
  }
  @keyframes floating {
    0%,
    100% {
      transform: translate(0, -30px);
    }
    50% {
      transform: translate(0, 25px);
    }
  }
  .floating-bottom {
    animation-name: floating-bottom;
    animation-duration: 5s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
  }
  @keyframes floating-bottom {
    0%,
    100% {
      transform: translate(0, -35px);
    }
    50% {
      transform: translate(0, 25px);
    }
  }

  ${reveal("connectorPasswordless")}
`;

/* ── 3. capabilities — sticky left column, cards scrolling past on the right ─ */

export const CapabilitiesSection = styled("section")`
  ${ROW_ALIGN_START__SPACE_B}
  ${PAGE_CONTAINER}
  position: relative;
  margin-bottom: 427px;
  ${MAGNET}
  ${REDUCED_MOTION}

  @media (max-width: ${breakpoints.m1024}px) {
    margin-bottom: 213px;
  }
  @media (max-width: ${breakpoints.sm}px) {
    flex-direction: column;
    align-items: center;
    gap: 64px;
    margin-bottom: 120px;
  }
  @media (max-width: ${breakpoints.xxs}px) {
    gap: 56px;
    margin-bottom: 96px;
  }

  & .left-column {
    max-width: 526px;
    ${STICKY_POSITION}
    flex-shrink: 1.1;
    top: ${(props) =>
      props?.screenHeight > 950 ? "calc(50vh - 320px)" : "125px"};
    ${COLUMN_ALIGN_START__JUSTIFY_START}
    gap: 48px;
    @media (max-width: ${breakpoints.sm}px) {
      position: static;
      align-items: center;
      text-align: center;
      max-width: 100%;
    }
  }

  & .right-column {
    max-width: 639px;
    ${COLUMN_CENTER}
    gap: 96px;
    z-index: 1;
    @media (min-width: ${breakpoints.sm}px) {
      padding-top: 120px;
      padding-bottom: 200px;
    }
    @media (max-width: ${breakpoints.sm}px) {
      gap: 64px;
      width: 100%;
      max-width: 100%;
    }
  }

  & .connector-badge-text {
    ${EYEBROW}
    color: var(--Blue);
  }

  & .section-title {
    width: 100%;
    ${TEXT_56}
    font-weight: 700;
    color: var(--Black);
    @media (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.xxl}px) {
      ${TEXT_50}
      letter-spacing: -0.05em;
    }
    @media (max-width: ${breakpoints.sm}px) {
      ${TEXT_40}
      line-height: 56px;
      text-align: center;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      ${TEXT_40}
      line-height: 48px;
      letter-spacing: -0.07em;
      text-align: center;
    }
  }

  & .gradient-font {
    ${GRADIENT_CLAUSE}
  }

  & .section-body {
    ${GREY_BODY}
    @media (max-width: ${breakpoints.sm}px) {
      text-align: center;
    }
  }

  & .card {
    ${COLUMN_ALIGN_START__JUSTIFY_START}
    gap: 48px;
    width: 100%;
    background: var(--White);
    box-shadow: 0px 32px 48px 0px #101e420d;
    border-radius: 24px;
    padding: 86px;
    @media (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.xxl}px) {
      padding: 43px;
    }
    @media (max-width: ${breakpoints.sm}px) {
      padding: 43px;
      gap: 40px;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      padding: 0;
      box-shadow: none;
      border-radius: 0;
      gap: 32px;
    }
  }

  & .card-title {
    max-width: 463px;
    ${TEXT_36}
    font-weight: 700;
    color: var(--Black);
    @media (max-width: ${breakpoints.xxs}px) {
      ${TEXT_32}
      line-height: 38px;
    }
  }

  & .card-body {
    ${TEXT_18}
    font-weight: 500;
    color: var(--Grey);
    @media (max-width: ${breakpoints.xxs}px) {
      ${TEXT_16_H24}
      text-align: left;
    }
  }

  & ul {
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    & li {
      ${TEXT_20_500}
      color: var(--Black);
      margin-left: 48px;
      position: relative;
      @media (max-width: ${breakpoints.xxs}px) {
        ${TEXT_18}
        font-weight: 500;
        margin-left: 40px;
      }
    }
  }
  & .bullet-login li:before {
    ${BULLET_STYLE}
    background-image: url(${bulletFirst});
  }
  & .bullet-kyc li:before {
    ${BULLET_STYLE}
    background-image: url(${bulletSecond});
  }
  & .bullet-kyb li:before {
    ${BULLET_STYLE}
    background-image: url(${bulletThird});
  }
  & ul li:before {
    @media (max-width: ${breakpoints.xxs}px) {
      left: -40px;
    }
  }

  & .contained-button {
    ${PRIMARY_BUTTON}
  }
  & .button-label {
    ${BUTTON_LABEL}
  }

  ${reveal("connectorCapabilities")}
`;

/* ── 4. quickstart ──────────────────────────────────────────────────────── */

export const QuickstartSection = styled("section")`
  ${COLUMN_ALIGN_CENTER__SPACE_B}
  ${PAGE_CONTAINER}
  gap: 96px;
  margin-bottom: 314px;
  ${REDUCED_MOTION}

  @media (max-width: ${breakpoints.m1024}px) {
    margin-bottom: 180px;
  }
  @media (max-width: ${breakpoints.sm}px) {
    gap: 64px;
    margin-bottom: 140px;
  }
  @media (max-width: ${breakpoints.xxs}px) {
    gap: 56px;
    margin-bottom: 120px;
  }

  & .top-frame {
    ${COLUMN_ALIGN_CENTER__SPACE_B}
    gap: 56px;
    width: 100%;
    @media (max-width: ${breakpoints.xxs}px) {
      gap: 40px;
    }
  }

  & .connector-badge-text {
    ${EYEBROW}
    color: var(--Blue);
    text-align: center;
  }

  & .section-title {
    ${TEXT_56}
    font-weight: 700;
    color: var(--Black);
    text-align: center;
    max-width: 900px;
    @media (max-width: ${breakpoints.sm}px) {
      ${TEXT_40}
      line-height: 56px;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      ${TEXT_36}
      line-height: 44px;
      letter-spacing: -0.07em;
    }
  }

  & .gradient-font {
    ${GRADIENT_CLAUSE}
  }

  & .section-body {
    ${GREY_BODY}
    text-align: center;
    max-width: 822px;
  }

  & .steps {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 48px;
    width: 100%;
    @media (max-width: ${breakpoints.sm}px) {
      grid-template-columns: 1fr;
      gap: 56px;
    }
  }

  & .step {
    ${COLUMN_ALIGN_START__JUSTIFY_START}
    gap: 24px;
    border-top: 1px solid var(--Solitude);
    padding-top: 32px;
  }

  & .step-index {
    ${TEXT_11}
    text-transform: uppercase;
    color: var(--Blue);
  }

  & .step-title {
    ${TEXT_24}
    font-weight: 700;
    color: var(--Black);
    text-align: left;
  }

  & .step-body {
    ${TEXT_18}
    font-weight: 500;
    color: var(--Grey);
    @media (max-width: ${breakpoints.xxs}px) {
      ${TEXT_16_H24}
      text-align: left;
    }
  }

  ${reveal("connectorQuickstart")}
`;

/* ── 5. FAQ — the sticky-left split the landing page's Q&A uses ─────────── */

export const FaqSection = styled("section")`
  ${ROW_ALIGN_START__SPACE_B}
  ${PAGE_CONTAINER}
  margin-bottom: 357px;
  ${MAGNET}
  ${REDUCED_MOTION}

  @media (max-width: ${breakpoints.m1024}px) {
    margin-bottom: 180px;
  }
  @media (max-width: ${breakpoints.sm}px) {
    flex-direction: column;
    align-items: center;
    gap: 56px;
    margin-bottom: 167px;
  }
  @media (max-width: ${breakpoints.xxs}px) {
    margin-bottom: 114px;
  }

  & .left-column {
    max-width: 416px;
    ${STICKY_POSITION}
    top: ${(props) =>
      props?.screenHeight > 850 ? "calc(50vh - 288.5px)" : "125px"};
    ${COLUMN_ALIGN_START__JUSTIFY_START}
    gap: 48px;
    @media (max-width: ${breakpoints.sm}px) {
      position: static;
      align-items: center;
      text-align: center;
      max-width: 100%;
    }
  }

  & .right-column {
    max-width: 636px;
    width: 100%;
    @media (min-width: ${breakpoints.sm}px) {
      padding-top: 120px;
      padding-bottom: 200px;
    }
  }

  & .connector-badge-text {
    ${EYEBROW}
    color: var(--Blue);
  }

  & .section-title {
    ${TEXT_56}
    font-weight: 700;
    color: var(--Black);
    @media (max-width: ${breakpoints.sm}px) {
      ${TEXT_40}
      line-height: 56px;
      text-align: center;
    }
    @media (max-width: ${breakpoints.xxs}px) {
      ${TEXT_40}
      line-height: 48px;
      letter-spacing: -0.07em;
      text-align: center;
    }
  }

  & .gradient-font {
    ${GRADIENT_CLAUSE}
  }

  & .section-body {
    ${GREY_BODY}
    @media (max-width: ${breakpoints.sm}px) {
      text-align: center;
    }
  }

  & .WBorder-button {
    ${GHOST_BUTTON}
  }
  & .button-label {
    ${BUTTON_LABEL}
  }

  ${reveal("connectorFaq")}
`;
